import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LogIn, CheckCircle, Users } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';

const InvitePage = () => {
  const { groups, userId, joinGroup, requestToJoin, fetchGroup } = useAppContext();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [groupInfo, setGroupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requested, setRequested] = useState(false);

  const alreadyMember = groups.some(g => g.id === groupId);

  useEffect(() => {
    const load = async () => {
      if (alreadyMember) {
        const g = groups.find(g => g.id === groupId);
        setGroupInfo(g);
      } else {
        const g = await fetchGroup(groupId);
        setGroupInfo(g);
        if (g?.pendingRequests?.some(r => r.userId === userId)) {
          setRequested(true);
        }
      }
      setLoading(false);
    };
    load();
  }, [groupId, alreadyMember, groups, fetchGroup, userId]);

  if (loading) {
    return (
      <>
        <Header showBack onBack={() => navigate('/')} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </>
    );
  }

  if (!groupInfo) {
    return (
      <>
        <Header showBack onBack={() => navigate('/')} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Grupo nao encontrado</h2>
          <p className="text-slate-400 text-sm mb-6">O link pode estar errado ou o grupo foi removido.</p>
          <Button onClick={() => navigate('/')}>Voltar ao Inicio</Button>
        </div>
      </>
    );
  }

  if (alreadyMember) {
    return (
      <>
        <Header showBack onBack={() => navigate('/')} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Voce ja eh membro!</h2>
          <p className="text-slate-400 text-sm mb-6">Voce ja participa do grupo <strong>{groupInfo.name}</strong>.</p>
          <Button onClick={() => navigate('/')}>Ir para Home</Button>
        </div>
      </>
    );
  }

  const isPrivateGroup = groupInfo.isPrivate;

  const handleJoin = async () => {
    if (isPrivateGroup) {
      await joinGroup(groupId);
      navigate('/');
    } else {
      await requestToJoin(groupId);
      setRequested(true);
    }
  };

  return (
    <>
      <Header showBack onBack={() => navigate('/')} />
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-center text-white">
            <div className="text-4xl mb-3">⚽</div>
            <h2 className="text-xl font-bold">{groupInfo.name}</h2>
            <p className="text-blue-100 text-sm mt-1">{groupInfo.sport}</p>
          </div>
          <div className="p-6 space-y-4">
            {groupInfo.city && (
              <div className="text-sm text-slate-500 text-center">📍 {[groupInfo.city, groupInfo.courtName].filter(Boolean).join(' - ')}</div>
            )}
            <div className="text-sm text-slate-400 text-center flex items-center justify-center gap-1">
              <Users className="w-4 h-4" /> {groupInfo.memberIds?.length || 1} membro{(groupInfo.memberIds?.length || 1) !== 1 ? 's' : ''}
            </div>
            {requested ? (
              <div className="text-center py-4">
                <CheckCircle className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Solicitacao enviada!</p>
                <p className="text-xs text-slate-400 mt-1">O admin ira analisar sua solicitacao.</p>
              </div>
            ) : (
              <Button className="w-full" onClick={handleJoin}>
                <LogIn className="w-4 h-4" /> {isPrivateGroup ? 'Entrar no Grupo' : 'Solicitar Entrada'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InvitePage;
